import joi from 'joi';
import z from 'zod';
import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  Cursor,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import { $bool } from '../zod';
import {
  AddIncomingWebhookBody,
  IncomingEventProcessingWebhookAttributes,
  UpdateIncomingWebhookBody,
} from './types/incoming-webhook';
import {
  AdjustBalancePayload,
  BurnRuleResult,
  EventReportAttributes,
  EventReportStatus,
  OfferRewardPayload,
} from './event-processing.type';
import { CompleteImpactAttributes } from './types/rule';
import { EventName, EventSource } from '../rule-api-client';
import { ProfileLevel } from '../profile-api-client/types/profile';

export const defaultEventReportStatus = [
  EventReportStatus.success,
  EventReportStatus.error,
  EventReportStatus.orphan,
  EventReportStatus.denied,
];

export const $reportsFilters = joi
  .object<GetEventReportsFilters>({
    statuses: joi
      .array()
      .items(
        joi
          .string()
          .valid(...Object.values(EventReportStatus))
          .empty(''),
      )
      .optional()
      .default(defaultEventReportStatus),
    programIds: joi.array().items(joi.string().empty('')).optional().default([]),
    sources: joi.array().items(joi.string().empty('')).optional().default([]),
    eventNames: joi.array().items(joi.string().empty('')).optional().default([]),
    endDate: joi.string().isoDate().optional().empty('').allow(null),
    startDate: joi.string().isoDate().optional().empty('').allow(null),
    limit: joi.number().optional().empty('').default(10),
    isTestUser: joi.boolean().optional().empty(''),
    isRepublished: joi.boolean().optional().empty(''),
    identity: joi.string().optional().empty(''),
    profileId: joi.string().uuid().optional().empty(''),
    clientEventId: joi.string().optional().empty(''),
  })
  .required();

export interface GetEventReportsFilters {
  statuses?: (string | number)[];
  programIds?: string[];
  sources?: EventSource[];
  eventNames?: EventName[];
  endDate?: Date;
  startDate?: Date;
  limit?: number;
  isTestUser?: boolean;
  isRepublished?: boolean;
  identity?: string;
  profileId?: string;
  clientEventId?: string;
}

export const $searchReportsQuery = z.object({
  cursor: z.string(),
  count: $bool.default(false).optional(),
});

export type SearchReportsRawQuery = z.infer<typeof $searchReportsQuery>;

export class EventProcessingApiClient extends ApiClient {
  static serviceName = 'event-processing-api';

  constructor(http?: AxiosInstance, context?: ApiClientContext, options?: ApiClientOptions) {
    super('/api/event-processing', http, context, options);
  }

  async findAvailableBurnRules(
    programId: string,
    profileId: string,
    options?: RouteOptions,
  ): Promise<CompleteImpactAttributes> {
    const { data } = await this.http.get(
      `/programs/${programId}/profiles/${profileId}/burn`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async processBurnRule(
    programId: string,
    profileId: string,
    ruleId: string,
    options?: RouteOptions,
  ): Promise<BurnRuleResult> {
    const { data } = await this.http.post(
      `/programs/${programId}/profiles/${profileId}/burn/${ruleId}/execute`,
      {},
      defaultAxiosConfig(options),
    );
    return data;
  }

  async offerReward(
    programId: string,
    profileId: string,
    payload: OfferRewardPayload,
    options?: RouteOptions,
  ): Promise<unknown> {
    return this.http.post(
      `/programs/${programId}/profiles/${profileId}/rewards`,
      payload,
      defaultAxiosConfig(options),
    );
  }

  async adjustBalance(
    programId: string,
    profileId: string,
    payload: AdjustBalancePayload,
    options?: RouteOptions,
  ): Promise<unknown> {
    return this.http.put(
      `/programs/${programId}/profiles/${profileId}/balance`,
      payload,
      defaultAxiosConfig(options),
    );
  }

  async updateLevel(
    programId: string,
    profileId: string,
    options?: RouteOptions,
  ): Promise<ProfileLevel> {
    return this.http
      .put<ProfileLevel>(
        `/programs/${programId}/profiles/${profileId}/level`,
        {},
        defaultAxiosConfig(options),
      )
      .then(({ data }) => data);
  }

  async countEventReports(
    accountId: string,
    filters: SearchReportsRawQuery,
    options?: RouteOptions,
  ): Promise<number> {
    const { data } = await this.http.get(`/accounts/${accountId}/eventReports`, {
      ...defaultAxiosConfig(options),
      params: {
        ...filters,
        count: true,
      },
    });
    return data;
  }

  async getEventReports(
    accountId: string,
    filters: SearchReportsRawQuery,
    options?: RouteOptions,
  ): Promise<Cursor<EventReportAttributes>> {
    const { data } = await this.http.get(`/accounts/${accountId}/eventReports`, {
      ...defaultAxiosConfig(options),
      params: {
        ...filters,
        count: false,
      },
    });
    return data;
  }

  async resetProgram(programId: string, options?: RouteOptions): Promise<void> {
    await this.http.delete(`/programs/${programId}/reset`, defaultAxiosConfig(options));
  }

  async getIncomingWebhooks(
    programId: string,
    options?: RouteOptions,
  ): Promise<IncomingEventProcessingWebhookAttributes[]> {
    const { data } = await this.http.get(
      `/programs/${programId}/incoming-webhooks`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async addIncomingWebhook(
    webhookId: string,
    programId: string,
    body: AddIncomingWebhookBody,
    options?: RouteOptions,
  ): Promise<IncomingEventProcessingWebhookAttributes> {
    const { data } = await this.http.post(
      `/programs/${programId}/incoming-webhooks/${webhookId}`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getIncomingWebhook(
    webhookId: string,
    options?: RouteOptions,
  ): Promise<IncomingEventProcessingWebhookAttributes> {
    const { data } = await this.http.get(
      `/incoming-webhooks/${webhookId}`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async updateIncomingWebhook(
    webhookId: string,
    body: UpdateIncomingWebhookBody,
    options?: RouteOptions,
  ): Promise<IncomingEventProcessingWebhookAttributes> {
    const { data } = await this.http.put(
      `/incoming-webhooks/${webhookId}`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async deleteIncomingWebhook(webhookId: string, options?: RouteOptions): Promise<void> {
    await this.http.delete(`/incoming-webhooks/${webhookId}`, defaultAxiosConfig(options));
  }

  async deleteProgramIncomingWebhooks(
    programId: string,
    options?: RouteOptions,
  ): Promise<{ affected: number }> {
    const { data } = await this.http.delete(
      `/programs/${programId}/incoming-webhooks`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async hasOrphans(programId: string, profileId: string, options?: RouteOptions): Promise<boolean> {
    const { data } = await this.http.get(
      `/programs/${programId}/profiles/${profileId}/eventReports/orphans/has`,
      defaultAxiosConfig(options),
    );
    return data;
  }
}
